// Premium Home Page - 2026 Cinematic Anime Streaming Experience
// Simplified but feature-rich implementation

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Stack,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayArrow,
  Visibility,
  ChevronLeft,
  ChevronRight,
  MoreHoriz,
  TrendingUp,
  LocalFireDepartment,
  NewReleases,
  StarRate,
  CalendarMonth,
  Movie,
  Tv,
  LiveTv,
  Bookmark,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchTrending, fetchPopular, fetchLatest, fetchTopRated, fetchSeasonal, fetchMovies, fetchOvas, fetchOnas, getGenres } from '../../hooks/useApi';
import { AnimeCard } from '../../components/ui/Card';
import { EnhancedSection } from '../../components/layout/EnhancedSection';
import { designTokens } from '../../theme/tokens';

// Mouse follow effect for parallax
let useMouseFollow = () => {
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return position;
};

export default function Home() {
  const navigate = useNavigate();
  const mousePos = useMouseFollow();

  // Fetch data
  const {
    data: trending = [],
    isLoading: loadingTrending,
  } = useQuery({
    queryKey: ['trending'],
    queryFn: () => fetchTrending(1, 8),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: popular = [],
    isLoading: loadingPopular,
  } = useQuery({
    queryKey: ['popular'],
    queryFn: () => fetchPopular(1, 12),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: latest = [],
    isLoading: loadingLatest,
  } = useQuery({
    queryKey: ['latest'],
    queryFn: () => fetchLatest(1, 12),
    staleTime: 2 * 60 * 1000,
  });

  const {
    data: topRated = [],
    isLoading: loadingTopRated,
  } = useQuery({
    queryKey: ['topRated'],
    queryFn: () => fetchTopRated(1, 12),
    staleTime: 10 * 60 * 1000,
  });

  const {
    data: seasonal = [],
    isLoading: loadingSeasonal,
  } = useQuery({
    queryKey: ['seasonal'],
    queryFn: () => fetchSeasonal(1, 20),
    staleTime: 10 * 60 * 1000,
  });

  const {
    data: movies = [],
    isLoading: loadingMovies,
  } = useQuery({
    queryKey: ['movies'],
    queryFn: () => fetchMovies(1, 12),
    staleTime: 10 * 60 * 1000,
  });

  // Hero carousel
  const [heroIndex, setHeroIndex] = React.useState(0);
  const [heroDirection, setHeroDirection] = React.useState(0);

  React.useEffect(() => {
    if (trending.length === 0) return;
    const id = setInterval(() => {
      setHeroDirection(1);
      setHeroIndex((prev) => (prev + 1) % trending.length);
    }, 8000);
    return () => clearInterval(id);
  }, [trending.length]);

  React.useEffect(() => {
    const timer = setTimeout(() => setHeroDirection(0), 800);
    return () => clearTimeout(timer);
  }, [heroIndex]);

  const handleGenreClick = (genre: string) => {
    navigate(`/browse?genre=${encodeURIComponent(genre)}`);
  };

  // Enhanced hero slide
  const renderHeroSlide = (anime: typeof trending[0], index: number) => {
    const isActive = index === heroIndex;
    const isExiting = index === (heroIndex - 1 + trending.length) % trending.length && heroDirection === 1;
    const isEntering = index === (heroIndex + 1) % trending.length && heroDirection === 1;
    const isExitingLeft = index === (heroIndex + 1) % trending.length && heroDirection === -1;
    const isEnteringLeft = index === (heroIndex - 1 + trenth.length) % trending.length && heroDirection === -1;

    let transform = 'translateX(0)';
    let opacity = isActive ? 1 : 0;
    let zIndex = isActive ? 2 : 1;

    if (isExiting) transform = 'translateX(-100%)';
    if (isEntering) transform = 'translateX(100%)';
    if (isExitingLeft) transform = 'translateX(100%)';
    if (isEnteringLeft) transform = 'translateX(-100%)';

    const genres = (anime.genres || []).slice(0, 3).map(g => typeof g === 'string' ? g : g.name);

    return (
      <motion.div
        key={anime.id}
        style={{
          position: 'absolute',
          inset: 0,
          opacity,
          zIndex,
          transform,
          transition: heroDirection !== 0 ? { duration: 0.8, ease: [0.175, 0.885, 0.32, 1.275] } : { duration: 0 },
        }}
      >
        {/* Background layers */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
          }}
        >
          {/* Parallax background */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${anime.cover || anime.poster})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.2) blur(20px)',
              transform: `scale(1.1) translate3d(${mousePos.x * 0.05}%, ${mousePos.y * 0.05}%, 0)`,
              transition: 'transform 0.5s ease-out',
              opacity: isActive ? 1 : 0.3,
            }}
          />

          {/* Gradient overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(45deg, rgba(5,5,5,0.7) 0%, rgba(0,0,0,0.8) 50%, rgba(5,5,5,0.7) 100%)',
              backgroundSize: '200% 200%',
              animation: 'gradientShift 8s ease infinite',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            position: 'absolute',
            bottom: '8%',
            left: '5%',
            right: '5%',
            color: 'white',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.2,
            maxWidth: 700,
          }}
        >
          {/* Genre badge */}
          <AnimatePresence>
            {isActive && (
              <motion.div
                key={`genre-${anime.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Chip
                  label={anime.type}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 26,
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Title */}
          <AnimatePresence>
            {isActive && (
              <motion.h1
                key={`title-${anime.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                sx={{
                  fontSize: { xs: '1.8rem', md: '2.8rem', lg: '3.2rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  textShadow: '0 4px 20px rgba(0,0,0,0.5)',
                  background: 'linear-gradient(90deg, #00b4d8, #8a2be2, #00ffef)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                }}
              >
                {anime.title}
                {/* Animated underline */}
                <span
                  style={{
                    display: 'block',
                    height: '3px',
                    width: '50%',
                    background: 'linear-gradient(90deg, transparent, #00b4d8, #8a2be2, #00ffef, transparent)',
                    backgroundSize: '200% 100%',
                    animation: 'gradientShift 3s ease infinite',
                    borderRadius: 1.5,
                    marginTop: 6,
                  }}
                />
              </motion.h1>
            )}
          </AnimatePresence>

          {/* Metadata */}
          <AnimatePresence>
            {isActive && (
              <motion.div
                key={`meta-${anime.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Chip
                  icon={<StarRate fontSize="small" />}
                  label={anime.score.toFixed(1)}
                  size="small"
                  variant="filled"
                  color="warning"
                  sx={{ fontWeight: 700 }}
                />
                <Chip label={anime.type} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }} />
                <Chip label={`${anime.episodes} eps`} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }} />
                {anime.year && <Chip label={anime.year.toString()} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }} />}
                {genres.map((g, i) => (
                  <Chip key={i} label={g} size="small" variant="filled" color="primary" sx={{ fontSize: '0.65rem', height: 22 }} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Description */}
          <AnimatePresence>
            {isActive && (
              <motion.p
                key={`desc-${anime.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                sx={{
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  lineHeight: 1.5,
                  color: 'rgba(255,255,255,0.85)',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  maxWidth: 600,
                  textShadow: '0 2px 6px rgba(0,0,0,0.4)',
                }}
              >
                {anime.synopsis}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Action buttons */}
          <AnimatePresence>
            {isActive && (
              <motion.div
                key={`actions-${anime.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                sx={{
                  display: 'flex',
                  gap: 0.8,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<PlayArrow fontSize="inherit" />}
                  onClick={() => navigate(`/anime/${anime.id}`)}
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  Watch Now
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<Visibility fontSize="inherit" />}
                  onClick={() => navigate(`/watch/${anime.id}`)}
                  sx={{
                    px: 3,
                    py: 1,
                    borderColor: 'rgba(255,255,255,0.4)',
                    color: 'white',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Trailer
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Bookmark fontSize="inherit" />}
                  onClick={() => {/* Handle bookmark */}}
                  sx={{
                    px: 2,
                    py: 0.5,
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.85rem',
                    '&:hover': {
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  Bookmark
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Floating particles */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: Math.random() * 4 + 1,
                height: Math.random() * 4 + 1,
                backgroundColor: ['#00b4d8', '#8a2be2', '#00ffef', '#ff00ff'][Math.floor(Math.random() * 4)],
                borderRadius: '50%',
                left: Math.random() * 100,
                top: Math.random() * 100,
                opacity: Math.random() * 0.4 + 0.1,
                animation: `
                  float ${Math.random() * 4 + 2}s ease-in-out ${Math.random() * 3}s infinite,
                  pulse ${Math.random() * 2 + 1}s ease-in-out ${Math.random() * 1}s infinite
                `,
                filter: 'blur(0.5px)',
              }}
            />
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, width: '100%', overflowX: 'hidden', position: 'relative' }}>
      {/* Animated background */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -2,
          overflow: 'hidden',
        }}
      >
        {/* Moving gradient background */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(45deg, #050505, #0a0a0a, #050505)',
            backgroundSize: '300% 300%',
            animation: 'gradientShift 15s ease infinite',
            zIndex: -1,
          }}
        />

        {/* Subtle nebula */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 30%, rgba(0,180,216,0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(138,43,226,0.05) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(0,255,239,0.05) 0%, transparent 50%),
              radial-gradient(circle at 60% 20%, rgba(255,0,255,0.05) 0%, transparent 50%)
            `,
            opacity: 0.2,
            zIndex: -1,
          }}
        />
      </div>

      {/* Floating stars */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: Math.random() * 1.5,
              height: Math.random() * 1.5,
              backgroundColor: '#ffffff',
              borderRadius: '50%',
              left: Math.random() * 100,
              top: Math.random() * 100,
              animation: `
                twinkle ${Math.random() * 3 + 2}s ease-in-out ${Math.random() * 2}s infinite
              `,
            }}
          />
        ))}
      </div>

      {/* Main hero section */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '65vh', md: '75vh' },
          minHeight: 500,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {trending.slice(0, 3).map((anime, index) => renderHeroSlide(anime, index))}

          {/* Navigation arrows */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 11,
              display: 'flex',
              gap: 1.5,
              left: { xs: 12, md: 20 },
              right: { xs: 'auto', md: 20 },
              justifyContent: { xs: 'center', md: 'flex-start' },
              width: { xs: 'calc(100% - 24px)', md: 'auto' },
              pointerEvents: 'none',
            }}
          >
            <motion.button
              onClick={() => {
                setHeroDirection(-1);
                setHeroIndex((prev) => (prev - 1 + trending.length) % trending.length);
              }}
              style={{
                pointerEvents: 'auto',
                width: 50,
                height: 50,
                borderRadius: '50%',
                backgroundColor: 'rgba(5,5,5,0.4)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                boxShadow: '0 0 15px rgba(0,0,0,0.2)',
              }}
              whileHover={{
                scale: 1.05,
                backgroundColor: 'rgba(0,180,216,0.25)',
                borderColor: 'transparent',
                boxShadow: '0 0 20px rgba(0,180,216,0.4)',
              }}
              whileTap={{ scale: 0.95 }}
              aria-label="Previous"
            >
              <ChevronLeft fontSize="medium" />
            </motion.button>
            <motion.button
              onClick={() => {
                setHeroDirection(1);
                setHeroIndex((prev) => (prev + 1) % trending.length);
              }}
              style={{
                pointerEvents: 'auto',
                width: 50,
                height: 50,
                borderRadius: '50%',
                backgroundColor: 'rgba(5,5,5,0.4)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                boxShadow: '0 0 15px rgba(0,0,0,0.2)',
              }}
              whileHover={{
                scale: 1.05,
                backgroundColor: 'rgba(0,180,216,0.25)',
                borderColor: 'transparent',
                boxShadow: '0 0 20px rgba(0,180,216,0.4)',
              }}
              whileTap={{ scale: 0.95 }}
              aria-label="Next"
            >
              <ChevronRight fontSize="medium" />
            </motion.button>
          </Box>

          {/* Progress dots */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 6,
              zIndex: 11,
            }}
          >
            {trending.slice(0, 3).map((_, i) => (
              <motion.div
                key={i}
                onClick={() => {
                  setHeroDirection(i > heroIndex ? 1 : -1);
                  setHeroIndex(i);
                }}
                style={{
                  width: i === heroIndex ? 8 : 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: i === heroIndex ? 'var(--anime-token-brand-primary-blue)' : 'rgba(255,255,255,0.25)',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                }}
                whileHover={{ scale: 1.4 }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Content sections */}
      <Container sx={{ pt: 6, pb: 8 }} maxWidth="1400px">
        {/* Continue Watching */}
        <EnhancedSection
          title="Continue Watching"
          variant="glass"
          animateChildren
          id="continue-watching"
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
            }}
          >
            {latest.slice(0, 8).map((anime) => (
              <motion.div
                key={anime.id}
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.03 * (latest.indexOf(anime)) }}
                sx={{
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <AnimeCard
                  {...anime}
                  variant="glass"
                  onClick={() => navigate(`/anime/${anime.id}`)}
                  badge={anime.episodes > 1 ? `Ep ${Math.min(anime.episodes, 12)}` : undefined}
                  showActions={false}
                />
              </motion.div>
            ))}
            {latest.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="h5">Nothing to continue</Typography>
                <Typography variant="body2" mt={2}>
                  Start watching to see your progress here
                </Typography>
              </Box>
            )}
          </Box>
        </EnhancedSection>

        {/* Trending Now */}
        <EnhancedSection
          title="Trending Now"
          variant="glass"
          animateChildren
          id="trending"
          action={
            <Button
              variant="outlined"
              size="small"
              startIcon={<TrendingUp fontSize="small" />}
              onClick={() => navigate('/browse?sort=trending')}
            >
              View All
            </Button>
          }
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
            }}
          >
            {loadingTrending ? Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ minHeight: 120 }}>
                <Skeleton variant="rectangular" height={120} />
              </Box>
            )) : trending.slice(0, 12).map((anime) => (
              <motion.div
                key={anime.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.02 * (trending.indexOf(anime)) }}
              >
                <AnimeCard
                  {...anime}
                  variant="default"
                  onClick={() => navigate(`/anime/${anime.id}`)}
                />
              </motion.div>
            ))}
          </Box>
        </EnhancedSection>

        {/* Popular This Week */}
        <EnhancedSection
          title="Popular This Week"
          variant="glass"
          animateChildren
          id="popular"
          action={
            <Button
              variant="outlined"
              size="small"
              startIcon={<LocalFireDepartment fontSize="small" />}
              onClick={() => navigate('/browse?sort=popularity')}
            >
              View All
            </Button>
          }
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
            }}
          >
            {loadingPopular ? Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ minHeight: 120 }}>
                <Skeleton variant="rectangular" height={120} />
              </Box>
            )) : popular.slice(0, 12).map((anime) => (
              <motion.div
                key={anime.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.02 * (popular.indexOf(anime)) }}
              >
                <AnimeCard
                  {...anime}
                  variant="default"
                  badge="Popular"
                  onClick={() => navigate(`/anime/${anime.id}`)}
                />
              </motion.div>
            ))}
          </Box>
        </EnhancedSection>

        {/* Latest Episodes */}
        <EnhancedSection
          title="Latest Episodes"
          variant="glass"
          animateChildren
          id="latest"
          action={
            <Button
              variant="outlined"
              size="small"
              startIcon={<NewReleases fontSize="small" />}
              onClick={() => navigate('/browse?sort=latest')}
            >
              View All
            </Button>
          }
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
            }}
          >
            {loadingLatest ? Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ minHeight: 120 }}>
                <Skeleton variant="rectangular" height={120} />
              </Box>
            )) : latest.slice(0, 12).map((anime) => (
              <motion.div
                key={anime.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.02 * (latest.indexOf(anime)) }}
              >
                <AnimeCard
                  {...anime}
                  variant="default"
                  badge="New"
                  onClick={() => navigate(`/anime/${anime.id}`)}
                />
              </motion.div>
            ))}
          </Box>
        </EnhancedSection>

        {/* Top Rated */}
        <EnhancedSection
          title="Top Rated"
          variant="glass"
          animateChildren
          id="top-rated"
          action={
            <Button
              variant="outlined"
              size="small"
              startIcon={<StarRate fontSize="small" />}
              onClick={() => navigate('/browse?sort=rating')}
            >
              View All
            </Button>
          }
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
            }}
          >
            {loadingTopRated ? Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ minHeight: 120 }}>
                <Skeleton variant="rectangular" height={120} />
              </Box>
            )) : topRated.slice(0, 12).map((anime, index) => (
              <motion.div
                key={anime.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.02 * (topRated.indexOf(anime)) }}
              >
                <AnimeCard
                  {...anime}
                  variant="default"
                  badge={`#${index + 1}`}
                  onClick={() => navigate(`/anime/${anime.id}`)}
                />
              </motion.div>
            ))}
          </Box>
        </EnhancedSection>

        {/* Movies */}
        {movies.length > 0 && (
          <EnhancedSection
            title="Movies"
            variant="glass"
            animateChildren
            id="movies"
            action={
              <Button
                variant="outlined"
                size="small"
                startIcon={<Movie fontSize="small" />}
                onClick={() => navigate('/movies')}
              >
                View All
              </Button>
            }
          >
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
              }}
            >
              {movies.slice(0, 12).map((anime) => (
                <motion.div
                  key={anime.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.02 * (movies.indexOf(anime)) }}
                >
                  <AnimeCard
                    {...anime}
                    variant="default"
                    badge="Movie"
                    onClick={() => navigate(`/anime/${anime.id}`)}
                  />
                </motion.div>
              ))}
            </Box>
          </EnhancedSection>
        )}

        {/* Currently Airing */}
        <EnhancedSection
          title="Currently Airing"
          variant="glass"
          animateChildren
          id="airing"
          action={
            <Button
              variant="outlined"
              size="small"
              startIcon={<LiveTv fontSize="small" />}
              onClick={() => navigate('/schedule')}
            >
              View Schedule
            </Button>
          }
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
            }}
          >
            {loadingSeasonal ? Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ minHeight: 120 }}>
                <Skeleton variant="rectangular" height={120} />
              </Box>
            )) : seasonal.slice(0, 12).map((anime) => (
              <motion.div
                key={anime.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.02 * (seasonal.indexOf(anime)) }}
              >
                <AnimeCard
                  {...anime}
                  variant="default"
                  onClick={() => navigate(`/anime/${anime.id}`)}
                />
              </motion.div>
            ))}
          </Box>
        </EnhancedSection>

        {/* Genres */}
        <EnhancedSection
          title="Browse by Genre"
          variant="glass"
          animateChildren
          id="genres"
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: -1,
            }}
          >
            {getGenres().map((genre) => (
              <motion.div
                key={genre}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Chip
                  label={genre}
                  onClick={() => handleGenreClick(genre)}
                  variant="outlined"
                  size="medium"
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 500,
                    height: 32,
                    borderColor: 'var(--anime-token-border-default)',
                    color: 'var(--anime-token-text-secondary)',
                    backgroundColor: 'var(--anime-token-surface-default)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'var(--anime-token-brand-primary-blue)',
                      color: 'var(--anime-token-brand-primary-blue)',
                      backgroundColor: 'rgba(0,180,216,0.05)',
                    },
                  }}
                />
              </motion.div>
            ))}
          </Box>
        </EnhancedSection>

        {/* Footer CTA */}
        <Box
          sx={{
            py: 8,
            px: 4,
            textAlign: 'center',
            backgroundColor: 'var(--anime-token-surface-variant)',
            borderTop: '1px solid var(--anime-token-divider)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Floating glow behind CTA */}
          <div
            style={{
              position: 'absolute',
              top: -50,
              left: -50,
              width: 200,
              height: 200,
              background: 'radial-gradient(circle, rgba(0,180,216,0.1) 0%, transparent 70%)',
              animation: 'float 6s ease-in-out infinite',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: 'linear-gradient(90deg, #00b4d8, #8a2be2, #00ffef, #ff00ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Ready to watch?
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 4,
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Create an account to track your progress, build your watchlist, and get personalized recommendations.
            </Typography>
            <Button
              variant="gradient"
              size="large"
              onClick={() => navigate('/register')}
              startIcon={<PlayArrow fontSize="inherit" />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                },
              }}
            >
              Start Free
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// Add keyframes for animations
const style = `
  @keyframes gradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes twinkle {
    0%, 100% {
      opacity: 0.3;
    }
    50% {
      opacity: 1;
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 0.6;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.1);
    }
  }
`;

// Inject styles into document
if (typeof document !== 'undefined') {
  const styleTag = document.createElement('style');
  styleTag.textContent = style;
  document.head.appendChild(styleTag);
}